import { getProfileAvatarUrl } from "../utils/userDisplay";
import Avatar from "../components/ds/Avatar";
import Badge from "../components/ds/Badge";
import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSocket } from "../context/SocketContext";
import { useTranslation } from "../context/TranslationContext";
import { messagePreview } from "../utils/chatFiles";
import { conversationPreview, isGroupChat, upsertGroup } from "../utils/groupChats";
import NewGroupModal from "../components/NewGroupModal";
import GroupAvatar from "../components/GroupAvatar";
import { getConversations, getMessageRequests, respondToMessageRequest } from "../api/messages";
import ChatWindow from "../components/ChatWindow";
import { FiCheck, FiX } from "react-icons/fi";
import UserProfileLink from "../components/UserProfileLink";
import FullScreenLayer from "../components/shell/FullScreenLayer";
import { useIsDesktop } from "../components/shell/useShellData";
import EmptyState from "../components/ds/EmptyState";
import Icon from "../components/ds/Icon";
import IconButton from "../components/ds/IconButton";
import SearchField from "../components/ds/SearchField";
import SegmentedControl from "../components/ds/SegmentedControl";
import { belongsInInbox, conversationForRequest, requestState } from "../utils/messageRequests";

const MessagesPage = ({ isDark, setToast }) => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { socket } = useSocket();
  const { t } = useTranslation();
  const desktop = useIsDesktop();
  const [allConversations, setAllConversations] = useState([]);
  const [receivedRequests, setReceivedRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("conversations"); // "conversations" or "requests"
  const [showRequests, setShowRequests] = useState(
    () => new URLSearchParams(location.search).get("requests") === "1"
  );
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [query, setQuery] = useState("");
  const [newGroupOpen, setNewGroupOpen] = useState(
    () => new URLSearchParams(location.search).get("newGroup") === "1"
  );

  const baseText = "text-label-primary";
  const mutedText = "text-label-secondary";
  const cardBase = "border-separator-default bg-bg-raised";

  // Track online users via Socket.io
  useEffect(() => {
    if (!socket) return;

    const handleUserOnline = ({ userId }) => {
      setOnlineUsers((prev) => new Set([...prev, userId]));
    };

    const handleUserOffline = ({ userId }) => {
      setOnlineUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    };

    const handleNewMessageRequest = ({ request, conversation }) => {
      // Only add if it's a pending request
      if (request.status === "pending") {
        // Its chat too, so the request opens with every message in it
        if (conversation?._id) {
          setAllConversations((prev) =>
            prev.some((c) => String(c._id) === String(conversation._id))
              ? prev
              : [
                  {
                    ...conversation,
                    unreadCount: conversation.lastMessage ? 1 : 0,
                    request: { _id: request._id, from: request.from, to: request.to, status: request.status },
                  },
                  ...prev,
                ]
          );
        }
        // Add to received requests
        setReceivedRequests((prev) => {
          // Check if request already exists
          if (prev.some(r => r._id === request._id)) {
            return prev;
          }
          return [request, ...prev];
        });
        // Show notification
        if (setToast) {
          const senderName = request.from?.username || request.from?.businessName || "Someone";
          setToast(t("messages.newMessageRequestFrom", { name: senderName }) || `New message request from ${senderName}`);
        }
      }
    };

    // Someone answered a request this user sent
    const answeredAs = (status) => ({ requestId }) => {
      markRequest(requestId, status);
      setSentRequests((prev) =>
        prev.map((r) => (String(r._id) === String(requestId) ? { ...r, status } : r))
      );
    };
    const handleRequestAccepted = answeredAs("accepted");
    const handleRequestRejected = answeredAs("rejected");

    // Groups: one started with this user, or one they're in that changed, or one they've left.
    const handleGroupUpdated = ({ conversation }) => {
      if (conversation?._id) setAllConversations((prev) => upsertGroup(prev, conversation));
    };
    const handleGroupRemoved = ({ conversationId: goneId }) => {
      setAllConversations((prev) => prev.filter((c) => String(c._id) !== String(goneId)));
    };
    socket.on("group_updated", handleGroupUpdated);
    socket.on("group_removed", handleGroupRemoved);

    socket.on("user_online", handleUserOnline);
    socket.on("user_offline", handleUserOffline);
    socket.on("new_message_request", handleNewMessageRequest);
    socket.on("message_request_accepted", handleRequestAccepted);
    socket.on("message_request_rejected", handleRequestRejected);

    return () => {
      socket.off("group_updated", handleGroupUpdated);
      socket.off("group_removed", handleGroupRemoved);
      socket.off("user_online", handleUserOnline);
      socket.off("user_offline", handleUserOffline);
      socket.off("new_message_request", handleNewMessageRequest);
      socket.off("message_request_accepted", handleRequestAccepted);
      socket.off("message_request_rejected", handleRequestRejected);
    };
  }, [socket, setToast]);

  // Fetch conversations
  useEffect(() => {
    const fetchConversations = async () => {
      try {
        setLoading(true);
        const response = await getConversations();
        if (response.success) {
          setAllConversations(response.data || []);
        }
      } catch (error) {
        console.error("Error fetching conversations:", error);
        if (setToast) {
          setToast(t("messages.failedToLoadConversations") || "Failed to load conversations");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchConversations();
    const interval = setInterval(fetchConversations, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [setToast]);

  // Fetch message requests (both received and sent)
  useEffect(() => {
    const fetchRequests = async () => {
      try {
        // Fetch received requests (only pending)
        const receivedResponse = await getMessageRequests("received");
        if (receivedResponse.success) {
          // Filter to ensure only pending requests (backend should handle this, but double-check)
          const pending = (receivedResponse.data || []).filter(req => req.status === "pending");
          setReceivedRequests(pending);
        }
        
        // Fetch sent requests (all statuses)
        const sentResponse = await getMessageRequests("sent");
        if (sentResponse.success) {
          setSentRequests(sentResponse.data || []);
        }
      } catch (error) {
        console.error("Error fetching message requests:", error);
      }
    };

    fetchRequests();
  }, []);

  // Sync showRequests with URL when navigating with ?requests=1
  useEffect(() => {
    setShowRequests(new URLSearchParams(location.search).get("requests") === "1");
    if (new URLSearchParams(location.search).get("newGroup") === "1") setNewGroupOpen(true);
  }, [location.search]);

  const closeNewGroup = () => {
    setNewGroupOpen(false);
    if (new URLSearchParams(location.search).get("newGroup") === "1") navigate("/messages", { replace: true });
  };

  const groupCreated = (conversation) => {
    setNewGroupOpen(false);
    setAllConversations((prev) => upsertGroup(prev, conversation));
    window.dispatchEvent(new CustomEvent("messages-updated"));
    navigate(`/messages/${conversation._id}`);
  };

  /** This user left a group, from inside it. */
  const groupLeft = (goneId) => {
    setAllConversations((prev) => prev.filter((c) => String(c._id) !== String(goneId)));
    window.dispatchEvent(new CustomEvent("messages-updated"));
    navigate("/messages");
  };

  // Load conversation when conversationId changes (search in allConversations to include active ones)
  useEffect(() => {
    if (conversationId && allConversations.length > 0) {
      const id = String(conversationId);
      const conversation = allConversations.find(
        (c) => (c._id && String(c._id)) === id
      );
      // Requests open like any chat: the sender keeps writing, the other side reads and answers
      setSelectedConversation(conversation || null);
    } else if (!conversationId) {
      setSelectedConversation(null);
    }
  }, [conversationId, allConversations]);

  /** A request's chat after an answer: accepted chats join the inbox, declined ones stay out of it. */
  function markRequest(requestId, status) {
    setAllConversations((prev) =>
      prev.map((c) =>
        c.request && String(c.request._id) === String(requestId)
          ? {
              ...c,
              status: status === "accepted" ? "active" : c.status,
              request: { ...c.request, status },
            }
          : c
      )
    );
  }

  /** After this user answers a request, from its card or from inside the chat. */
  const applyRequestAnswer = (requestId, action, data) => {
    const status = action === "accept" ? "accepted" : "rejected";
    setReceivedRequests((prev) => prev.filter((r) => String(r._id) !== String(requestId)));
    markRequest(requestId, status);
    // The chat may not be listed yet (a request from before this page loaded)
    const conv = data?.conversation;
    if (conv?._id) {
      setAllConversations((prev) =>
        prev.some((c) => String(c._id) === String(conv._id)) ? prev : [{ ...conv, unreadCount: 0 }, ...prev]
      );
    }
    // The side panel and header count requests too
    window.dispatchEvent(new CustomEvent("messages-updated"));
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      const response = await respondToMessageRequest(requestId, "accept");
      if (response.success) {
        applyRequestAnswer(requestId, "accept", response.data);
        if (response.data.conversation) {
          navigate(`/messages/${response.data.conversation._id}`);
        }
        if (setToast) {
          setToast(t("messages.requestAccepted") || "Message request accepted");
        }
      }
    } catch (error) {
      console.error("Error accepting request:", error);
      if (setToast) {
        setToast(t("messages.failedToAccept") || "Failed to accept request");
      }
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      const response = await respondToMessageRequest(requestId, "reject");
      if (response.success) {
        applyRequestAnswer(requestId, "reject");
        if (setToast) {
          setToast(t("messages.requestRejected") || "Message request rejected");
        }
      }
    } catch (error) {
      console.error("Error rejecting request:", error);
      if (setToast) {
        setToast(t("messages.failedToReject") || "Failed to reject request");
      }
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return t("messages.justNow") || "Just now";
    if (minutes < 60) return t("messages.minutesShort", { minutes }) || `${minutes}m`;
    if (hours < 24) return t("messages.hoursShort", { hours }) || `${hours}h`;
    if (days < 7) return t("messages.daysShort", { days }) || `${days}d`;
    return date.toLocaleDateString();
  };

  const isUserOnline = (userId) => {
    return onlineUsers.has(userId);
  };

  const getOtherParticipant = (conversation) => {
    const userId = user?.id ?? user?._id;
    return conversation.participants.find(
      (p) => String(p._id ?? p.id) !== String(userId)
    );
  };

  const myId = user?.id ?? user?._id;
  // The inbox includes requests this user sent; ones sent to them wait under Requests.
  const search = query.trim().toLowerCase();
  const nameMatches = (name) => !search || String(name || "").toLowerCase().includes(search);
  const personMatches = (person) => nameMatches(person?.username) || nameMatches(person?.businessName);
  const conversations = allConversations
    .filter((c) => belongsInInbox(c, myId))
    .filter((c) => (isGroupChat(c) ? nameMatches(c.name) : personMatches(getOtherParticipant(c))))
    .sort((a, b) => {
      const aTime = a.lastMessageAt ? new Date(a.lastMessageAt) : new Date(a.updatedAt);
      const bTime = b.lastMessageAt ? new Date(b.lastMessageAt) : new Date(b.updatedAt);
      return bTime - aTime;
    });

  const openRequestChat = (request) => {
    const conv = conversationForRequest(allConversations, request);
    if (conv) navigate(`/messages/${conv._id}`);
  };

  const isIncomingRequest = selectedConversation && !belongsInInbox(selectedConversation, myId);

  // Beside the list from desktop up; below that, a screen with nothing but the chat.
  const placeThread = (chat) =>
    desktop ? (
      <div className="min-h-0 min-w-0 flex-1">{chat}</div>
    ) : (
      <FullScreenLayer isDark={isDark} label={t("messages.messages")}>
        {chat}
      </FullScreenLayer>
    );

  return (
    <div className="flex h-[calc(100vh-220px)] min-h-[480px] w-full flex-col lg:h-[calc(100vh-128px)]">
      <NewGroupModal open={newGroupOpen} onClose={closeNewGroup} onCreated={groupCreated} isDark={isDark} setToast={setToast} />

      <div className="flex min-h-0 flex-1 gap-space-24 overflow-hidden">
        {/* Conversations */}
        <div
          className={`w-full shrink-0 flex-col gap-space-12 overflow-hidden lg:flex lg:w-[360px] lg:rounded-radius-lg lg:bg-bg-raised lg:p-space-16 ${
            selectedConversation ? "hidden" : "flex"
          }`}
        >
          <div className="flex items-center justify-between gap-space-8">
            <h1 className="text-title-2 text-label-primary">{t("messages.messages")}</h1>
            <IconButton label={t("groups.newGroup")} onClick={() => setNewGroupOpen(true)}>
              <Icon name="edit" size={18} />
            </IconButton>
          </div>
          <SearchField
            className="shrink-0 lg:hidden"
            placeholder={t("common.search")}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <SegmentedControl
            track="material"
            label={t("messages.messages")}
            value={showRequests ? "requests" : "messages"}
            onChange={(view) => setShowRequests(view === "requests")}
            options={[
              { value: "messages", label: t("messages.messages") },
              {
                value: "requests",
                label: receivedRequests.length > 0 ? `${t("messages.requests")} (${receivedRequests.length})` : t("messages.requests"),
              },
            ]}
          />
          {!showRequests && (
            <p className="hidden text-footnote text-label-secondary lg:block">
              {conversations.length}{" "}
              {conversations.length === 1 ? t("messages.conversation") || "conversation" : t("messages.conversations") || "conversations"}
            </p>
          )}
            {showRequests ? (
              <div className="min-h-0 flex-1 overflow-y-auto">
                
                {/* Received Requests */}
                {receivedRequests.length > 0 && (
                  <div className="mb-6">
                    <h3 className={`text-footnote-semibold uppercase tracking-wider mb-3 ${mutedText}`}>
                      {t("messages.received")} ({receivedRequests.length})
                    </h3>
                    <div className="space-y-3">
                      {receivedRequests.filter((request) => personMatches(request.from)).map((request) => {
                      // The chat it opened: every message sent so far, readable before answering
                      const chat = conversationForRequest(allConversations, request);
                      const latest = (chat?.lastMessage && messagePreview(chat.lastMessage, t)) || request.message;
                      return (
                      <div
                        key={request._id}
                        onClick={() => openRequestChat(request)}
                        className={`rounded-radius-md border p-4 transition-all ${chat ? "cursor-pointer" : ""} ${
                          "border-separator-default bg-bg-raised hover:bg-fill-quaternary"
                        }`}
                      >
                        <div className="mb-3 flex items-center gap-3">
                          <UserProfileLink
                            user={request.from}
                            avatarSize="lg"
                            isDark={isDark}
                            nameClassName={`text-subheadline-semibold ${baseText}`}
                            stopPropagation
                            fallback="User"
                          />
                          <div className="ml-auto flex shrink-0 items-center gap-2">
                            {chat?.unreadCount > 0 && (
                              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-accent-default px-1 text-footnote-semibold text-accent-on">
                                {chat.unreadCount}
                              </span>
                            )}
                            <p className={`text-footnote ${mutedText}`}>
                              {formatTime(chat?.lastMessageAt || request.createdAt)}
                            </p>
                          </div>
                        </div>
                        {latest && (
                          <p className={`text-subheadline mb-3 truncate ${mutedText}`}>{latest}</p>
                        )}
                        <div className="flex gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAcceptRequest(request._id);
                            }}
                            className="flex-1 flex items-center justify-center gap-2 rounded-radius-sm bg-accent-default text-accent-on px-3 py-2 text-subheadline-semibold transition"
                          >
                            <FiCheck className="h-4 w-4 flex-shrink-0" />
                            <span className="text-left">{t("messages.accept")}</span>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRejectRequest(request._id);
                            }}
                            className={`flex-1 flex items-center justify-center gap-2 rounded-radius-sm border px-3 py-2 text-subheadline-semibold transition ${
                              "border-separator-default bg-bg-raised hover:border-status-danger text-status-danger"
                            }`}
                          >
                            <FiX className="h-4 w-4 flex-shrink-0" />
                            <span className="text-left">{t("messages.reject")}</span>
                          </button>
                        </div>
                      </div>
                      );
                      })}
                    </div>
                  </div>
                )}

                {/* Sent Requests */}
                {sentRequests.length > 0 && (
                  <div>
                    <h3 className={`text-footnote-semibold uppercase tracking-wider mb-3 ${mutedText}`}>
                      {t("messages.sent")} ({sentRequests.length})
                    </h3>
                    <div className="space-y-3">
                      {sentRequests.filter((request) => personMatches(request.to)).map((request) => (
                        <div
                          key={request._id}
                          onClick={() => openRequestChat(request)}
                          className={`rounded-radius-md border p-4 transition-all ${
                            conversationForRequest(allConversations, request) ? "cursor-pointer" : ""
                          } ${
                            "border-separator-default bg-bg-raised hover:bg-fill-quaternary"
                          }`}
                        >
                          <div className="mb-3 flex items-center gap-3">
                            <UserProfileLink
                              user={request.to}
                              avatarSize="lg"
                              isDark={isDark}
                              nameClassName={`text-subheadline-semibold ${baseText}`}
                              stopPropagation
                              fallback="User"
                            />
                            <p className={`ml-auto shrink-0 text-footnote ${mutedText}`}>
                              {formatTime(request.createdAt)}
                            </p>
                          </div>
                          {request.message && (
                            <p className={`text-subheadline mb-3 ${mutedText}`}>{request.message}</p>
                          )}
                          <div className={`flex items-center justify-center gap-2 rounded-radius-sm px-3 py-2 text-subheadline-semibold ${
                            request.status === "pending"
                              ? "bg-status-warning-bg text-status-warning border border-status-warning"
                              : request.status === "accepted"
                              ? "bg-status-success-bg text-status-success border border-status-success"
                              : "bg-status-danger-bg text-status-danger border border-status-danger"
                          }`}>
                            <span className="text-left">
                              {request.status === "pending" && t("messages.pending")}
                              {request.status === "accepted" && (t("messages.accepted") || "Accepted")}
                              {request.status === "rejected" && (t("messages.rejected") || "Rejected")}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Empty State */}
                {receivedRequests.length === 0 && sentRequests.length === 0 && (
                  <EmptyState icon="message" title={t("messages.noPendingRequests") || "No pending requests"} />
                )}
              </div>
            ) : (
              <div className="min-h-0 flex-1 overflow-y-auto">
                {loading ? (
                  <p className="text-subheadline text-label-secondary">{t("messages.loadingConversations") || "Loading conversations..."}</p>
                ) : conversations.length === 0 ? (
                  search ? (
                    <EmptyState icon="search" title={t("shell.noResults")} />
                  ) : (
                    <EmptyState
                      icon="message"
                      title={t("messages.noConversations")}
                      message={t("messages.startConversationToSee") || "Start a conversation to see it here"}
                    />
                  )
                ) : (
                  <div className="flex flex-col gap-space-4">
                    {conversations.map((conversation) => {
                      const otherParticipant = getOtherParticipant(conversation);
                      const lastMessage = conversation.lastMessage;
                      const isActive = conversationId && String(conversation._id) === String(conversationId);
                      // A request this user sent, still waiting or declined
                      const sentState = requestState(conversation, myId);

                      return (
                        <button
                          key={conversation._id}
                          onClick={() => {
                            setSelectedConversation(conversation);
                            navigate(`/messages/${conversation._id}`);
                          }}
                          className={`w-full rounded-[20px] px-space-12 py-space-8 text-left transition-colors lg:p-space-8 ${
                            isActive ? "bg-control-selected" : "hover:bg-fill-quaternary"
                          }`}
                        >
                          <div className="flex items-center gap-space-12">
                            {isGroupChat(conversation) ? (
                              <GroupAvatar size="xl" isDark={isDark} className="!size-12 !rounded-14 !ring-0" />
                            ) : (
                              <span className="relative shrink-0">
                                <Avatar size="l" src={getProfileAvatarUrl(otherParticipant)} name={otherParticipant?.username || otherParticipant?.businessName || "User"} />
                                {isUserOnline(otherParticipant?._id) && (
                                  <span
                                    className="absolute -bottom-px -right-px size-3 rounded-full border-2 border-bg-base bg-accent-default lg:border-bg-raised"
                                    title={t("messages.online") || "Online"}
                                  />
                                )}
                              </span>
                            )}
                            <div className="flex min-w-0 flex-1 flex-col gap-space-4">
                              <p className="truncate text-subheadline-semibold text-label-primary">
                                {isGroupChat(conversation)
                                  ? conversation.name
                                  : otherParticipant?.username || otherParticipant?.businessName || "User"}
                              </p>
                              <p className="truncate text-footnote text-label-secondary">
                                {[
                                  lastMessage &&
                                    (isGroupChat(conversation) ? conversationPreview(conversation, myId, t) : messagePreview(lastMessage, t)),
                                  formatTime(conversation.lastMessageAt),
                                ]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </p>
                              {isGroupChat(conversation) && (
                                <p className="hidden text-footnote text-label-secondary lg:block">
                                  {t("groups.memberCount", { count: conversation.participants?.length || 0 })}
                                </p>
                              )}
                            </div>
                            {sentState && (
                              <Badge size="s" tone={sentState === "sent" ? "warning" : "danger"}>
                                {sentState === "sent" ? t("messages.pending") : t("messages.rejected")}
                              </Badge>
                            )}
                            {conversation.unreadCount > 0 && (
                              <>
                                <span className="size-2 shrink-0 rounded-full bg-accent-default lg:hidden" aria-hidden="true" />
                                <span className="hidden h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-accent-default px-[6px] text-caption-1-semibold text-accent-on lg:flex">
                                  {conversation.unreadCount}
                                </span>
                              </>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Thread */}
          {selectedConversation ? (
            placeThread(
              <ChatWindow
                key={selectedConversation._id}
                fill
                conversation={selectedConversation}
                currentUser={user}
                isDark={isDark}
                setToast={setToast}
                onBack={() => {
                  setSelectedConversation(null);
                  navigate(isIncomingRequest ? "/messages?requests=1" : "/messages");
                }}
                onRequestAnswered={(action, data) => {
                  const requestId = selectedConversation.request?._id;
                  if (!requestId) return;
                  applyRequestAnswer(requestId, action, data);
                  if (action === "reject") navigate("/messages?requests=1");
                }}
                onGroupChanged={(next) => setAllConversations((prev) => upsertGroup(prev, next))}
                onGroupLeft={groupLeft}
              />
            )
          ) : (
            <div className="hidden min-w-0 flex-1 items-center justify-center rounded-radius-lg bg-bg-raised p-space-16 lg:flex">
              <EmptyState
                icon="message"
                title={t("messages.selectConversation") || "Select a conversation"}
                message={t("messages.chooseConversation") || "Choose a conversation from the list to start messaging"}
                className="max-w-[400px]"
              />
            </div>
          )}
        </div>
      </div>
  );
};

export default MessagesPage;

